import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Tabs, Tab, Card, CardContent, Button } from '@mui/material';
import { AttachMoney as AttachMoneyIcon, Business, Description, Person, LocationOn } from '@mui/icons-material';
import { getCustomerDetailsAdmin } from '../../../services/admin.service';
import { openBusinessDocumentFile } from '../../../services/customer.service';
import Loader from '../../../common/loader';
import { formatCurrency } from '../../../helper/currencyFormatter.helper';
import CustomerUserHeader from './customerDetails/user/CustomerUserHeader';
import CustomerFullDetailsCard from './customerDetails/user/CustomerFullDetailsCard';


const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('user');

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!id) return;
      try {
        const data = await getCustomerDetailsAdmin(id);
        setCustomer(data);
      } catch (error) {
        console.error('Error fetching customer details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomerDetails();
  }, [id]);

  if (loading) return <Loader />;

  if (!customer) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Customer not found</Typography>
      </Box>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        <CustomerUserHeader
          onBack={() => navigate(-1)}
        />

        <Box className="mb-3 bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-gray-100">
          <Tabs
            value={activeTab}
            onChange={handleChange}
            centered
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              "& .MuiTab-root": {
                minHeight: { xs: 70, sm: 80, md: 90 },
                minWidth: { xs: 80, sm: 100, md: 120 },
                borderRadius: { xs: "12px", sm: "16px" },
                marginX: { xs: 0.5, sm: 1 },
                textTransform: "none",
                fontWeight: 500,
                color: "#6b7280",
                transition: "all 0.3s ease",
                display: "flex",
                flexDirection: "column",
                gap: { xs: "4px", sm: "8px" },
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
                padding: { xs: "8px 4px", sm: "12px 8px" },
              },
              "& .MuiTab-root:hover": {
                backgroundColor: "rgba(37, 99, 235, 0.06)",
              },
              "& .Mui-selected": {
                backgroundColor: "primary.main",
                color: "white !important",
                boxShadow: "0 4px 12px rgba(37,99,235,0.18)",
                "& .MuiSvgIcon-root": {
                  color: "white !important",
                },
              },
              "& .MuiTabs-scrollButtons": {
                display: { xs: "flex", sm: "none" },
              },
              "& .MuiTabs-flexContainer": {
                justifyContent: { xs: "center", sm: "center" },
                gap: { xs: 0.5, sm: 1 },
              },
            }}
          >
            <Tab
              value="user"
              label={
                <div className="flex flex-col items-center">
                  <Person
                    className={`w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mb-0.5 sm:mb-1 ${
                      activeTab === "user" ? "text-white" : "text-blue-600"
                    }`}
                  />
                  <span className="text-xs sm:text-sm font-medium leading-tight">
                    User Info
                  </span>
                </div>
              }
            />
            <Tab
              value="docs"
              label={
                <div className="flex flex-col items-center">
                  <Description
                    className={`w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mb-0.5 sm:mb-1 ${
                      activeTab === "docs" ? "text-white" : "text-blue-600"
                    }`}
                  />
                  <span className="text-xs sm:text-sm font-medium leading-tight">
                    Prospect KYC
                  </span>
                </div>
              }
            />
            <Tab
              value="business"
              label={
                <div className="flex flex-col items-center">
                  <Business
                    className={`w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mb-0.5 sm:mb-1 ${
                      activeTab === "business" ? "text-white" : "text-blue-600"
                    }`}
                    fontSize="large"
                  />
                  <span className="text-xs sm:text-sm font-medium leading-tight">
                    Business
                  </span>
                </div>
              }
            />
          </Tabs>
        </Box>

        {activeTab === "user" && (
          <Box sx={{ mx: "auto", maxWidth: "100%" }}>
            <CustomerFullDetailsCard
              customer={customer}
            />
          </Box>
        )}

        {activeTab === "docs" && (
          <Box sx={{ mx: "auto", maxWidth: "100%" }}>
            {customer.prospectKyc ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 3, sm: 4, md: 1 },
                }}
              >

                {/* Identification Documents */}
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 10px 28px rgba(124, 58, 237, 0.10)",
                    border: "1px solid rgba(124, 58, 237, 0.14)",
                    overflow: "hidden",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background:
                        "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          bgcolor: "secondary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <Description sx={{ color: "white", fontSize: 24 }} />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "secondary.main" }}
                      >
                        Identification Documents
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            ID Type
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                          >
                            {customer.prospectKyc.idType || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            ID Number
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                          >
                            {customer.prospectKyc.idNumber || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            Expiration Date
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                          >
                            {customer.prospectKyc.expirationDate || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            Government ID
                          </Typography>
                          {customer.prospectKyc.govFile ? (
                            <Button
                              variant="outlined"
                              color="secondary"
                              startIcon={<Description />}
                              href={`${customer.prospectKyc.govFile}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                mt: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: "medium",
                                "&:hover": {
                                  bgcolor: "secondary.main",
                                  color: "white",
                                },
                              }}
                            >
                              View Document
                            </Button>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: "600",
                                fontSize: "1.1rem",
                                color: "text.secondary",
                              }}
                            >
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            Proof of Address
                          </Typography>
                          {customer.prospectKyc.addressFile ? (
                            <Button
                              variant="outlined"
                              color="secondary"
                              startIcon={<LocationOn />}
                              href={`${customer.prospectKyc.addressFile}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                mt: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: "medium",
                                "&:hover": {
                                  bgcolor: "secondary.main",
                                  color: "white",
                                },
                              }}
                            >
                              View Document
                            </Button>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: "600",
                                fontSize: "1.1rem",
                                color: "text.secondary",
                              }}
                            >
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Financial Information */}
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 10px 28px rgba(124, 58, 237, 0.10)",
                    border: "1px solid rgba(124, 58, 237, 0.14)",
                    overflow: "hidden",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background:
                        "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          bgcolor: "secondary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <AttachMoneyIcon
                          sx={{ color: "white", fontSize: 24 }}
                        />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "secondary.main" }}
                      >
                        Financial Information
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            Personal Income
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                          >
                            {customer.prospectKyc.personalIncome
                              ? `$${customer.prospectKyc.personalIncome}`
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            Business Revenue
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                          >
                            {customer.prospectKyc.businessRevenue
                              ? `$${customer.prospectKyc.businessRevenue}`
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "rgba(124, 58, 237, 0.04)",
                            border: "1px solid rgba(124, 58, 237, 0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: "medium" }}
                          >
                            Source of Funds
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                          >
                            {customer.prospectKyc.sourceOfFunds || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Card
                sx={{
                  borderRadius: { xs: 3, sm: 4 },
                  boxShadow: "0 10px 28px rgba(37, 99, 235, 0.10)",
                  textAlign: "center",
                  py: { xs: 6, sm: 8, md: 10 },
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  border: "2px dashed rgba(37, 99, 235, 0.18)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      width: { xs: 80, sm: 100 },
                      height: { xs: 80, sm: 100 },
                      bgcolor: "primary.main",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: { xs: 3, sm: 4 },
                      boxShadow: "0 8px 28px rgba(37, 99, 235, 0.18)",
                    }}
                  >
                    <Description
                      sx={{ fontSize: { xs: 40, sm: 50 }, color: "white" }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      color: "primary.main",
                      fontSize: { xs: "1.5rem", sm: "2.125rem" },
                    }}
                  >
                    No KYC Data Available
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{
                      mb: 3,
                      maxWidth: 600,
                      mx: "auto",
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                  >
                    This customer has not completed their Know Your Customer
                    verification process yet.
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      maxWidth: 500,
                      mx: "auto",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    KYC information will appear here once the customer submits
                    their details through the prospect KYC form.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {activeTab === "business" && (
          <Box sx={{ mx: "auto" }}>
            {customer.customerBusinesses && customer.customerBusinesses.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {customer.customerBusinesses.map((business: any) => {
                  const address = [
                    business.street,
                    business.city,
                    business.state,
                    business.zipCode,
                  ]
                    .filter(Boolean)
                    .join(", ");

                  return (
                    <Card
                      key={business.id}
                      sx={{
                        borderRadius: 4,
                        boxShadow: "0 10px 28px rgba(37, 99, 235, 0.10)",
                        border: "1px solid rgba(37, 99, 235, 0.14)",
                        overflow: "hidden",
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background:
                            "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                          <Box
                            sx={{
                              width: { xs: 40, sm: 48 },
                              height: { xs: 40, sm: 48 },
                              borderRadius: "50%",
                              bgcolor: "primary.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 2,
                              flex: "0 0 auto",
                            }}
                          >
                            <Business sx={{ color: "white", fontSize: { xs: 22, sm: 26 } }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: "bold",
                                color: "primary.main",
                                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                wordBreak: "break-word",
                              }}
                            >
                              {business.companyName || "Business"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Added {business.createdAt ? new Date(business.createdAt).toLocaleDateString() : "N/A"}
                            </Typography>
                          </Box>
                        </Box>

                        <Grid container spacing={2}>
                          {[
                            ["Tax ID / EIN", business.taxIdEin],
                            ["Registration Type", business.registrationType],
                            ["Business Purpose", business.businessPurpose],
                            ["Address", address],
                            ["Ownership Details", business.ownershipDetails],
                          ].map(([label, value]) => (
                            <Grid size={{ xs: 12, md: label === "Ownership Details" || label === "Address" ? 6 : 4 }} key={label}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "rgba(37, 99, 235, 0.04)",
                                  border: "1px solid rgba(37, 99, 235, 0.10)",
                                  height: "100%",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1, fontWeight: "medium" }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: "600", wordBreak: "break-word" }}
                                >
                                  {value || "N/A"}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        <Box sx={{ mt: 4 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              fontWeight: "bold",
                              color: "primary.main",
                            }}
                          >
                            Loans
                          </Typography>
                          {business.loanDeals && business.loanDeals.length > 0 ? (
                            <Grid container spacing={2}>
                              {business.loanDeals.map((loanDeal: any) => (
                                <Grid size={{ xs: 12, md: 6 }} key={loanDeal.id}>
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: "rgba(37, 99, 235, 0.04)",
                                      border: "1px solid rgba(37, 99, 235, 0.10)",
                                      height: "100%",
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
                                    >
                                      {loanDeal.loanName || "Loan"}
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                      {[
                                        ["Deal Type", loanDeal.dealType],
                                        ["Loan Type", loanDeal.loanType],
                                        ["Loan Size", loanDeal.loanSize ? formatCurrency(loanDeal.loanSize) : "N/A"],
                                        ["Status", loanDeal.status],
                                        ["Max LTV", loanDeal.maxLTV ? `${loanDeal.maxLTV}%` : "N/A"],
                                        ["Min DSCR", loanDeal.minDSCRatio],
                                      ].map(([label, value]) => (
                                        <Grid size={{ xs: 12, sm: 6 }} key={label}>
                                          <Typography variant="body2" color="text.secondary">
                                            {label}
                                          </Typography>
                                          <Typography sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                                            {value || "N/A"}
                                          </Typography>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              No loans found for this business.
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ mt: 4 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              fontWeight: "bold",
                              color: "secondary.main",
                            }}
                          >
                            Business Documents
                          </Typography>
                          {business.documents && business.documents.length > 0 ? (
                            <Grid container spacing={2}>
                              {business.documents.map((doc: any) => (
                                <Grid size={{ xs: 12, md: 6 }} key={doc.id}>
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: "rgba(124, 58, 237, 0.04)",
                                      border: "1px solid rgba(124, 58, 237, 0.10)",
                                      height: "100%",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mb: 1, fontWeight: "medium" }}
                                    >
                                      {doc.docName || "Document"}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ mb: 1.5, fontWeight: 600, wordBreak: "break-word" }}
                                    >
                                      {doc.originalName || "N/A"}
                                    </Typography>
                                    {doc.filePath ? (
                                      <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Description />}
                                        onClick={() => openBusinessDocumentFile(doc.id)}
                                        sx={{
                                          borderRadius: 2,
                                          textTransform: "none",
                                          fontWeight: "medium",
                                        }}
                                      >
                                        View Document
                                      </Button>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No file uploaded
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              No documents found for this business.
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 10px 28px rgba(37, 99, 235, 0.10)",
                  textAlign: "center",
                  py: 10,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  border: "2px dashed rgba(37, 99, 235, 0.18)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      bgcolor: "primary.main",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 4,
                      boxShadow: "0 8px 28px rgba(37, 99, 235, 0.18)",
                    }}
                  >
                    <Business sx={{ fontSize: 50, color: "white" }} />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", mb: 2, color: "primary.main" }}
                  >
                    No Business Data Available
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 3, maxWidth: 600, mx: "auto" }}
                  >
                    This customer has not added any businesses yet.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

export default CustomerDetails;
